import React, {useEffect, useState, useCallback} from 'react';
import { EncodingStatus } from "../player/types";
import {KollabEncodingProgressProps} from "./types";
import {Tooltip} from "../hooks/useTooltip";

const POLL_INTERVAL = 5000;

/**
 * Component to query and show encoding progress for a file
 */
const KollabEncodingProgress = React.memo(function KollabEncodingProgress(
    props: KollabEncodingProgressProps,
) {
    const {
        fileId,
        doneClickCallback,
    } = props;

    const [tooltipText, setTooltipText] = useState<string>('Optimizing for playback...');
    const [status, setStatus] = useState<string>('queued');
    const [progress, setProgress] = useState<number>(0);

    const wrapperRef = React.useRef<HTMLAnchorElement>(null);
    const progressBarRef = React.useRef<HTMLElement>(null);

    const clearTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
    };

    useEffect(() => {
        progressBarRef.current?.setAttribute('progress', progress.toFixed(0));
    }, [progress]);

    const poll = useCallback(async () => {
        if (!fileId) return;
        try {
            const resp = await fetch(`/encoding/progress?id=${fileId}`);

            if (!resp.ok) {
                console.log(`Failed to get encoding progress: ${await resp.text()}`);
                return;
            }

            const data: EncodingStatus = await resp.json();
            setStatus(data.status);

            switch (data.status) {
                case 'queued': {
                    let position = data.position;
                    setTooltipText('Optimizing for playback... (queue position ' + (!position && position !== 0 ? 'unknown' : position) + ')');
                    setProgress(0);
                    break;
                }

                case 'processing': {
                    let percent = data.progress;
                    if (!percent) percent = 0;
                    setProgress(percent);
                    setTooltipText('Optimizing for playback... (' + percent + '%)');
                    break;
                }

                case 'done': {
                    clearTimer();
                    break;
                }
            }
        } catch {
            console.log(`Failed to get encoding progress.`);
        }
    }, [fileId]);

    const timerRef = React.useRef<TimerHandler>(null);

    useEffect(() => {
        poll().then(() => {
            if (status !== 'done') {
                // Setup timer
                timerRef.current = setInterval(poll, POLL_INTERVAL);
            }
        });

        // Clean up timer on unmount
        return () => {
            clearTimer();
        };
    }, []);

    if (status === 'done') {
        return (
            <Tooltip title="Load optimized version">
                <a className="horizontal" href="#" onClick={(e) => { if (doneClickCallback) doneClickCallback()}} role="status" aria-label="Optimization complete">
                    <div class="optimizing-success" style={{display: "inline-block", background: "url('img/Success.svg') no-repeat center/80%", width: '35px', height: '35px'}}></div>
                </a>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={tooltipText} >
            <progress-bar-circular
                ref={progressBarRef}
                className="optimizing-progress"
                width="35"
                height="35"
                image-url="img/Optimizing.svg"
                progress={progress}
            >
            </progress-bar-circular>
        </Tooltip>
    );
});

export default KollabEncodingProgress;
